import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { formatRatePercent } from "@/shared/utils/percent";
import type { PaymentStatement, StatementContent, StatementPayee } from "@/api/paymentStatement";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";

/**
 * The Payment Statement, rendered from the issued row and nothing else (Ticket-348).
 *
 * Rules this component must never break:
 *  - the tax row and its restatement are absent when the creator is not registered — never "0%";
 *  - the Administration Fee never appears;
 *  - no more of the bank account than the last four digits;
 *  - payment terms are shown as a number of days, never a due date;
 *  - the "not a tax invoice" footer is always present.
 */
export type StatementAudience = "creator" | "cm" | "finance";

interface Props {
	/** The issued row, or (with `preview`) the would-be content built by the same code that issues. */
	statement: PaymentStatement | StatementContent;
	audience: StatementAudience;
	/** Before send-to-finance: no number, date or status exist yet. */
	preview?: boolean;
	/** Creator only. Confirmation is its own ticket (CMVP-28); the control ships disabled. */
	onConfirm?: () => void;
	confirmDisabled?: boolean;
}

const money = (amount: number, currency: string) =>
	`${amount.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const formatDate = (value: string) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(date);
};

const ENTITY_LABEL: Record<StatementPayee["entityType"], string> = {
	individual: "Individual",
	company: "Company",
	trust: "Trust",
};

const addressLines = (payee: StatementPayee): string[] => {
	const locality = [payee.city, payee.state, payee.postalCode].filter(Boolean).join(" ");
	return [payee.addressLine1, payee.addressLine2, locality, payee.countryCode].filter((l): l is string => Boolean(l));
};

const TITLES: Record<StatementAudience, string> = {
	creator: "My Payment Statement",
	cm: "Finance Instruction Sheet",
	finance: "Payment Statement",
};

export const PaymentStatementView: React.FC<Props> = ({
	statement,
	audience,
	preview = false,
	onConfirm,
	confirmDisabled = true,
}) => {
	const hasTax = statement.taxRate !== null && statement.declaredTaxAmount !== null;
	const isCreator = audience === "creator";
	const issued = preview ? null : (statement as PaymentStatement);

	return (
		<Card aria-label="Payment Statement">
			<Body>
				<Header>
					<div>
						<Title>{TITLES[audience]}</Title>
						{issued ? (
							<Subtitle>
								<strong>{issued.statementNumber}</strong>
								<Dot aria-hidden="true">·</Dot>
								Issued {formatDate(issued.issuedAt)}
							</Subtitle>
						) : (
							<Subtitle>
								Preview · the statement number and issue date are assigned when this is sent to Finance
							</Subtitle>
						)}
					</div>
					{issued ? (
						<StatusPill data-testid="statement-status" $status={issued.status}>
							{issued.status}
						</StatusPill>
					) : (
						<StatusPill $status="preview">Preview</StatusPill>
					)}
				</Header>

				<Section>
					<SectionLabel>Issued by</SectionLabel>
					<Strong>{statement.issuerName}</Strong>
					<Muted>{statement.issuerRegistration}</Muted>
					<Muted>{statement.issuerAddress}</Muted>
				</Section>

				<Section>
					<SectionLabel>Payable to</SectionLabel>
					<Strong>{statement.payee.legalName}</Strong>
					<Muted>{ENTITY_LABEL[statement.payee.entityType]}</Muted>
					{statement.payee.registrationNumber ? (
						<Muted>
							{statement.payee.registrationNumberLabel} {statement.payee.registrationNumber}
						</Muted>
					) : null}
					<Muted>
						•••• {statement.payee.accountLast4} · {statement.payee.bankName}
					</Muted>
					{addressLines(statement.payee).map((line) => (
						<Muted key={line}>{line}</Muted>
					))}
				</Section>

				<Section>
					<SectionLabel>For</SectionLabel>
					<Strong>{statement.campaignName}</Strong>
					<Muted>
						Project code <strong>{statement.projectCode}</strong>
						{statement.marketName ? ` · ${statement.marketName}` : ""}
					</Muted>
					<Deliverables>
						{statement.deliverables.map((d) => (
							<li key={d.id}>
								{d.title} ×{d.quantity}
							</li>
						))}
					</Deliverables>
				</Section>

				<Money data-testid="statement-money">
					<MoneyRow>
						<span>Agreed fee</span>
						<Amount>{money(statement.principalAmount, statement.currency)}</Amount>
					</MoneyRow>
					{hasTax ? (
						<MoneyRow>
							<span>
								{statement.taxName} ({formatRatePercent(statement.taxRate as number)})
							</span>
							<Amount>{money(statement.declaredTaxAmount as number, statement.currency)}</Amount>
						</MoneyRow>
					) : null}
					<MoneyRow $total>
						<span>Amount Payable</span>
						<Amount>{money(statement.amountPayable, statement.currency)}</Amount>
					</MoneyRow>
				</Money>

				<Section>
					<SectionLabel>Payment terms</SectionLabel>
					<Strong>{statement.paymentTermsDays} days from the issue date of this statement</Strong>
				</Section>

				{statement.restatementText ? (
					<Restatement data-testid="statement-restatement">{statement.restatementText}</Restatement>
				) : null}

				<Declaration>{statement.creatorDeclarationText}</Declaration>
				<Footer>{statement.footerText}</Footer>
			</Body>

			{isCreator ? (
				<Actions>
					<HelpLink
						to="/account?tab=bank"
						data-testid="statement-something-wrong"
						title="To change your bank or tax details, update them in your profile"
					>
						Something wrong?
					</HelpLink>
					<ConfirmButton type="button" onClick={onConfirm} disabled={confirmDisabled}>
						Confirm
					</ConfirmButton>
				</Actions>
			) : null}
		</Card>
	);
};

const Card = styled.section`
	box-sizing: border-box;
	background: #f3f5ff;
	border: 1px solid #cdd4f6;
	border-radius: 16px;
	min-width: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.12);
`;
const Body = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 20px;
	padding: 24px;
`;
const Header = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
`;
const Title = styled.h2`
	margin: 0;
	color: ${CUSTOM_COLORS.PRIMARY};
	font:
		700 18px / normal "Poppins",
		sans-serif;
`;
const Subtitle = styled.p`
	margin: 4px 0 0;
	color: #707070;
	font-size: 13px;
	strong {
		color: #303030;
		font-weight: 600;
	}
`;
const Dot = styled.span`
	margin: 0 6px;
`;
const StatusPill = styled.span<{ $status: string }>`
	display: inline-flex;
	align-items: center;
	min-height: 27px;
	padding: 1px 12px;
	border-radius: 64px;
	border: 1px solid
		${({ $status }) => ($status === "confirmed" ? "#66b026" : $status === "rejected" ? "#ff4d4f" : "#cdd4f6")};
	background: #fff;
	color: #303030;
	font-size: 13px;
	font-weight: 600;
	text-transform: capitalize;
`;
const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;
const SectionLabel = styled.span`
	color: #707070;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	margin-bottom: 4px;
`;
const Strong = styled.span`
	color: #303030;
	font-size: 15px;
	font-weight: 600;
`;
const Muted = styled.span`
	color: #565656;
	font-size: 14px;
`;
const Deliverables = styled.ul`
	margin: 6px 0 0;
	padding-left: 18px;
	color: #565656;
	font-size: 14px;
	li {
		margin-bottom: 2px;
	}
`;
const Money = styled.div`
	display: flex;
	flex-direction: column;
	background: #fff;
	border: 1px solid #cdd4f6;
	border-radius: 12px;
	padding: 4px 16px;
`;
const MoneyRow = styled.div<{ $total?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 0;
	border-bottom: 1px solid #edf0fd;
	color: ${({ $total }) => ($total ? "#303030" : "#565656")};
	font-size: 14px;
	font-weight: ${({ $total }) => ($total ? 700 : 400)};
	&:last-child {
		border-bottom: 0;
	}
`;
const Amount = styled.span`
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`;
const Restatement = styled.p`
	margin: 0;
	padding: 12px 16px;
	background: #fff8e6;
	border: 1px solid #ffe0a3;
	border-radius: 12px;
	color: #8a5a00;
	font-size: 13px;
	line-height: 1.5;
`;
const Declaration = styled.p`
	margin: 0;
	color: #565656;
	font-size: 13px;
	line-height: 1.5;
`;
const Footer = styled.p`
	margin: 0;
	color: #707070;
	font-size: 12px;
	font-style: italic;
	line-height: 1.5;
`;
const Actions = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 24px;
	border-top: 1px solid #cdd4f6;
	background: #fff;
`;
const HelpLink = styled(Link)`
	color: #565656;
	font-size: 14px;
	text-decoration: underline dotted;
	cursor: help;
`;
const ConfirmButton = styled.button`
	min-height: 40px;
	padding: 0 32px;
	border: 0;
	border-radius: 64px;
	background: ${CUSTOM_COLORS.PRIMARY};
	color: #fff;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;
